import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import OkCancelModal from '@/shared/components/OkCancelModal';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import { SpinnerIcon } from '@/shared/components/Icons';
import { SharepointFolderTree } from '@/modules/documents/modals/SharepointFolderTree';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import messages from '../documentMessages';

const SharepointModal = ({ show, intl, documentId, onSave, onClose, mode, onLinkConfirmed }) => {
  const [sites, setSites] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [drivesLoading, setDrivesLoading] = useState(false);
  const [drives, setDrives] = useState([]);
  const [selectedDriveId, setSelectedDriveId] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('/');

  const isLinkMode = mode === 'link';

  useEffect(() => {
    setSitesLoading(true);
    api.base
      .get(`${config.appUrls.documents}/api/sharepoint/sites`)
      .then(
        x => {
          setSites(!x.data ? [] : x.data);
        },
        error => {
          handleHttpError(intl)(error);
        },
      )
      .finally(() => {
        setSitesLoading(false);
      });
  }, [intl]);

  useEffect(() => {
    setDrives([]);
    setSelectedDriveId(null);
    setSelectedFolderId('/');
    if (!selectedSiteId) return;

    setDrivesLoading(true);
    api.base
      .get(`${config.appUrls.documents}/api/sharepoint/sites/${selectedSiteId}/drives`)
      .then(
        x => {
          setDrives(!x.data ? [] : x.data);
        },
        error => {
          handleHttpError(intl)(error);
        },
      )
      .finally(() => {
        setDrivesLoading(false);
      });
  }, [selectedSiteId, intl]);

  const save = () => {
    if (isLinkMode) {
      return onLinkConfirmed({
        siteId: selectedSiteId,
        driveId: selectedDriveId ?? null,
        folderId: selectedFolderId === '/' ? null : selectedFolderId,
      });
    }

    return api.base
      .post(
        `${config.appUrls.documents}/api/documents/${documentId}/MoveToSharepoint?driveId=${selectedDriveId}&folderId=${selectedFolderId}`,
        {},
      )
      .then(
        ({ data }) => onSave(data),
        error => {
          handleHttpError(intl)(error);
        },
      );
  };

  const closeModal = () => {
    onClose();
  };

  if (!show) return null;

  const titleMessage = isLinkMode
    ? messages.LinkToSharepointLocation
    : messages.MoveToSharepoint;

  const title = () => {
    if (!sitesLoading && !drivesLoading && !foldersLoading)
      return intl.formatMessage(titleMessage);

    return (
      <>
        {intl.formatMessage(titleMessage)}
        <span className="margin5L">
          <SpinnerIcon />
        </span>
      </>
    );
  };

  const isDisabled = isLinkMode
    ? !selectedSiteId
    : !selectedSiteId || !selectedDriveId || !selectedFolderId;

  return (
    <OkCancelModal
      title={title()}
      show={show}
      onClose={closeModal}
      disabled={isDisabled}
      onSave={save}
      okLabel={isLinkMode ? intl.formatMessage(messages.Save) : undefined}
    >
      <div className="form-horizontal">
        <div>
          <div className="form-group">
            <label className="control-label col-md-3">
              <FormattedMessage {...messages.SharepointSite} />
            </label>
            <div className="col-md-9" style={{ marginTop: '7px' }}>
              <AutoComplete
                mode="edit"
                disabled={sitesLoading}
                items={sites.map(s => ({ key: s.id, label: s.name }))}
                value={selectedSiteId ? { key: selectedSiteId, label: sites.find(s => s.id === selectedSiteId)?.name ?? '' } : null}
                onChange={item => setSelectedSiteId(item?.key ?? null)}
              />
            </div>
            <label className="control-label col-md-3">
              <FormattedMessage {...messages.SharepointDrive} />
            </label>
            <div className="col-md-9" style={{ marginTop: '7px' }}>
              <AutoComplete
                mode="edit"
                disabled={!selectedSiteId || drivesLoading}
                items={drives.map(d => ({ key: d.id, label: d.name }))}
                value={selectedDriveId ? { key: selectedDriveId, label: drives.find(d => d.id === selectedDriveId)?.name ?? '' } : null}
                onChange={item => setSelectedDriveId(item?.key ?? null)}
              />
            </div>
            <label className="control-label col-md-3">
              <FormattedMessage {...messages.Folder} />
            </label>
            <div className="col-md-9" style={{ marginTop: '7px' }}>
              <SharepointFolderTree
                setLoading={setFoldersLoading}
                onSelectedFolderChanged={setSelectedFolderId}
                siteId={selectedSiteId}
                driveId={selectedDriveId}
              />
            </div>
          </div>
        </div>
      </div>
    </OkCancelModal>
  );
};

SharepointModal.propTypes = {
  show: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  onSave: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  documentId: PropTypes.string,
  mode: PropTypes.oneOf(['move', 'link']),
  onLinkConfirmed: PropTypes.func,
};

SharepointModal.defaultProps = {
  mode: 'move',
  onSave: undefined,
  onLinkConfirmed: undefined,
  documentId: undefined,
};

export default SharepointModal;
